import { useApolloClient, useQuery as useApolloQuery, useMutation } from '@apollo/client';
import { useFeatureFlag, useIsMobile } from '@newfront-insurance/admin-ui';
import { graphql } from '@newfront-insurance/graphql-frontend';
import type { AuthProviderContext } from '@newfront-insurance/next-auth';
import { useNotify } from '@newfront-insurance/next-notifications';
import { useQuery } from '@newfront-insurance/next-router-provider';
import type { SelectedAccount } from '@newfront-insurance/nova-core';
import { useProvider, type Provider } from '@newfront-insurance/react-provision';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { v4 } from 'uuid';

import { useMarkChatAsRead } from '../../../hooks/use-mark-chat-as-read';
import { useNovaSettings } from '../../../hooks/use-nova-settings';
import type { MessageReaction, UIMessageWithMetadata } from '../../../lib/types';
import { getChatPlaceholder } from '../../../lib/utils';
import type { ChatAppType } from '../../../queries/chats';
import {
  GET_ACCOUNT,
  GET_CHAT_WITH_MESSAGES,
  GET_CHATS_BY_FILTERS,
  GET_DELETED_CHAT_WITH_MESSAGES,
} from '../../../queries/chats';
import { msGraphManager } from '../../../services/ms-graph-manager';
import {
  ChatOverlayProvider,
  type ChatSettingsSection,
  type QueuedNewChatInput,
  type StartNewChatWithInputOptions,
} from '../contexts/chat-overlay-context';
import { useChatNotificationController } from '../hooks/use-chat-notification-controller';
import { useOutlookConnectController } from '../hooks/use-outlook-connect-controller';
import { resolveEffectiveAccountUuid, resolvePageAccountUuid } from '../lib/account-bootstrap';
import { clearActiveChatView, setActiveChatView } from '../lib/active-chat-views';
import { ACTIVE_CHAT_STATUS } from '../lib/chat-optimistic-updates';
import {
  getCurrentChatWithMessages,
  getIsMessagesLoading,
  getVisibleChatActivationKey,
  shouldMarkChatAsReadOnOpen,
} from '../lib/chat-query-state';
import { backendMessageToSDKMessage } from '../lib/chat-utils';
import { normalizeChatUuid } from '../lib/notification-helpers';
import type { ActiveChat, ChatError, RetryLastChatRequest } from '../lib/shared-types';
import {
  DEFAULT_NOVA_CHAT_COMPLETIONS_ENDPOINT,
  DURABLE_NOVA_CHAT_COMPLETIONS_ENDPOINT,
  isDurableActiveStreamAlreadyPersisted,
  isDurableActiveStreamBaseHydrated,
  isDurableNovaChatCompletionsEndpoint,
  probePersistedDurableActiveStream,
} from '../lib/transport';

const WTW_COLLEAGUE_AUTHORIZATION_TYPE = 'wtw_colleague';
const NOVA_NEWFRONT_MSAL_DELEGATED_GRAPH_CONNECTIONS_FEATURE_FLAG = 'nova-newfront-msal-delegated-graph-connections';
const DURABLE_STREAM_BASE_REFETCH_INTERVAL_MS = 500;
const MAX_DURABLE_STREAM_BASE_REFETCH_INTERVAL_MS = 5000;

const SET_CHAT_ACCOUNT_MUTATION = graphql(`
  mutation SetChatAccount($chatUuid: String!, $accountUuid: String!) {
    setChatAccount(chatUuid: $chatUuid, accountUuid: $accountUuid) {
      uuid
      account {
        uuid
        name
      }
    }
  }
`);

// Context for wrapper components to access Nova state
interface NovaWrapperState {
  isChatVisible: boolean;
  isFloatingButtonVisible: boolean;
  hasNotifications: boolean;
  handleToggle: () => void;
  handleOpen: () => void;
  handleClose: () => void;
}

const NovaWrapperContext = createContext<NovaWrapperState | undefined>(undefined);

export function useNovaWrapperState(): NovaWrapperState {
  const context = useContext(NovaWrapperContext);
  if (!context) {
    throw new Error('useNovaWrapperState must be used within NovaProvider');
  }
  return context;
}

export interface NovaProviderCallbacks {
  /** Called when starting a new chat */
  onStartNewChat?: (accountContext?: { uuid: string; name: string }) => void;
  /** Called when an account is selected */
  onAccountSelect?: (account: SelectedAccount) => void;
  /** Called when chat finishes */
  onFinish?: () => void;
  /** Called when chat errors */
  onError?: (error: Error) => void;
  /** Called when chat receives data */
  onData?: (dataPart: { type: string; data: unknown }) => void;
  /** Called when the active chat changes (e.g., from sidebar selection) */
  onChatChange?: (chatUuid: string) => void;
  /** Called when a user reacts to a message (thumbs up/down) */
  onReaction?: (reaction: MessageReaction | null, messageId: string) => void;
}

export interface NovaProviderProps {
  children: React.ReactNode;
  userUuid: string;
  scopes?: string[];
  authProvider: Provider<AuthProviderContext>;
  isStandaloneMode: boolean;
  isSidebarHidden?: boolean;
  initialAccountUuid?: string;
  initialChatUuid?: string;
  /** App origin for filtering chats (e.g. OUTLOOK) */
  appType?: ChatAppType;
  /** Called when chat visibility changes */
  onVisibilityChange?: (isVisible: boolean) => void;
  /** Called when chat should close (for custom close behavior) */
  onClose?: () => void;
  /** Called when chat overlay becomes visible - useful for refetching data and marking as read */
  onChatOverlayVisible?: (chatUuid: string) => void;
  callbacks?: NovaProviderCallbacks;
  /** Custom error message shown inline when a stream request fails */
  errorMessage?: string;
  /** Disables all write interactions and keeps the view read only */
  isReadOnly?: boolean;
  /** Allows the selected chat query to return a soft-deleted chat. */
  includeDeletedChat?: boolean;
  /**
   * Whether this provider owns browser notifications for the tab. Exactly one
   * mounted NovaProvider should own them; secondary embeds (e.g. a panel next
   * to the global overlay) pass false. Chats visible in a non-owner provider
   * are still excluded from notifications tab-wide.
   */
  enableNotifications?: boolean;
}

/**
 * Provider that encapsulates all chat overlay logic including:
 * - State management (visibility, activeChat, accountUuid, notifications, errors, sidebar, fullscreen)
 * - GraphQL queries (chat messages, account data)
 * - Event handlers (keyboard shortcuts, account selection, new chat)
 * - Context value creation
 *
 * Exposes callbacks for customization while handling all internal logic.
 */
export function NovaProvider({
  children,
  userUuid,
  scopes,
  authProvider,
  isStandaloneMode,
  isSidebarHidden,
  initialAccountUuid,
  initialChatUuid,
  onVisibilityChange,
  onClose,
  onChatOverlayVisible,
  callbacks,
  appType,
  errorMessage,
  isReadOnly = false,
  includeDeletedChat = false,
  enableNotifications = true,
}: NovaProviderProps) {
  const { isMobile } = useIsMobile();
  const { authState, getToken, userDetails } = useProvider(authProvider);
  const isPublicMode = !authState.isLoggedIn;
  const isWtwColleague = userDetails?.authorizationType === WTW_COLLEAGUE_AUTHORIZATION_TYPE;
  const canSelectAccount = !isWtwColleague;
  const canConnectOutlook = !isWtwColleague;
  const queryAccountUuid = useQuery<string | undefined>('accountUuid');

  // MS Graph API integration
  const msGraphEnabled = useFeatureFlag('nova-tools-graph-api', false);
  // Legacy Newfront uses users-service's MSAL token cache when this flag is on.
  // WTW already uses delegated token exchange and cannot manage Outlook here.
  const delegatedGraphConnectionsEnabled = useFeatureFlag(
    NOVA_NEWFRONT_MSAL_DELEGATED_GRAPH_CONNECTIONS_FEATURE_FLAG,
    false,
  );
  useEffect(() => {
    msGraphManager.setFeatureFlagEnabled(msGraphEnabled);
  }, [msGraphEnabled]);
  const showAllNovaChats = useFeatureFlag('show-all-nova-chats', false);
  const durableWorkflowsFlagEnabled = useFeatureFlag('nova-durable-workflows', false);
  const apiEndpoint = durableWorkflowsFlagEnabled
    ? DURABLE_NOVA_CHAT_COMPLETIONS_ENDPOINT
    : DEFAULT_NOVA_CHAT_COMPLETIONS_ENDPOINT;
  const effectiveAppType = showAllNovaChats ? null : (appType ?? undefined);

  // Internal state management
  const [isChatVisible, setIsChatVisible] = useState(isStandaloneMode ?? false);
  const [isFloatingButtonVisible] = useState(true);
  const [activeChat, setActiveChat] = useState<ActiveChat | undefined>(
    initialChatUuid ? { chatUuid: initialChatUuid, hasAnyMessages: false } : undefined,
  );
  const [activeAccountUuid, setActiveAccountUuid] = useState<string | undefined>(undefined);
  const [chatError, setChatError] = useState<ChatError | undefined>(undefined);
  const [retryLastRequest, setRetryLastRequestState] = useState<RetryLastChatRequest | undefined>(undefined);
  const [isRetryingLastRequest, setIsRetryingLastRequest] = useState(false);
  const [additionalContext, setAdditionalContext] = useState<Record<string, unknown>>({});
  const [pendingRegeneration, setPendingRegeneration] = useState<{
    chatUuid: string;
    message: UIMessageWithMetadata;
  }>();

  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile && !isSidebarHidden && !!isStandaloneMode);
  const [isFullscreen, setIsFullscreen] = useState(isStandaloneMode ?? false);
  const [selectedAccount, setSelectedAccount] = useState<SelectedAccount>(undefined);
  const [hasAnyMessages, setHasAnyMessages] = useState(false);
  const [queuedNewChatInput, setQueuedNewChatInput] = useState<QueuedNewChatInput | null>(null);
  const [isSettingsOpen, setIsSettingsOpenState] = useState(false);
  const [settingsSection, setSettingsSection] = useState<ChatSettingsSection>('general');
  const [settingsBackSignal, setSettingsBackSignal] = useState(0);
  const settingsCloseIntentRef = useRef<(() => void) | null>(null);
  const visibleChatActivationKeyRef = useRef<string | null>(null);
  const requestSettingsBack = useCallback(() => {
    settingsCloseIntentRef.current = null;
    setSettingsBackSignal((c) => c + 1);
  }, []);
  const requestSettingsClose = useCallback((afterClose?: () => void) => {
    settingsCloseIntentRef.current = afterClose ?? (() => undefined);
    setSettingsBackSignal((c) => c + 1);
  }, []);
  const [hasActiveSettingsSubView, setHasActiveSettingsSubView] = useState(false);

  const setIsSettingsOpen = useCallback((open: boolean) => {
    setIsSettingsOpenState(open);
    if (!open) {
      setSettingsSection('general');
    }
  }, []);

  const setRetryLastRequest = useCallback((retry?: RetryLastChatRequest) => {
    setRetryLastRequestState(() => retry);
  }, []);

  const handleRetryLastRequest = useCallback(() => {
    if (!retryLastRequest || isRetryingLastRequest) {
      return;
    }

    setIsRetryingLastRequest(true);
    setChatError(undefined);
    void Promise.resolve()
      .then(() => retryLastRequest())
      .catch(() => {
        setChatError({
          message: 'Unable to complete your request. Please try again.',
          timestamp: new Date(),
          type: 'server',
          canRetry: true,
        });
      })
      .finally(() => setIsRetryingLastRequest(false));
  }, [isRetryingLastRequest, retryLastRequest]);

  const notify = useNotify();

  const pageAccountUuid = resolvePageAccountUuid({ initialAccountUuid, queryAccountUuid });

  const chatUuid = useMemo(() => {
    return activeChat?.chatUuid || initialChatUuid || `new-chat-${v4()}`;
  }, [activeChat?.chatUuid, initialChatUuid]);
  const activeChatUuidRef = useRef(chatUuid);
  useLayoutEffect(() => {
    activeChatUuidRef.current = chatUuid;
  }, [chatUuid]);

  const { serverSettings, persistSetting, persistSettings } = useNovaSettings(userUuid);

  const handleSetActiveChat = useCallback(
    (chatUuidToSet: string, chatAccountUuid?: string | null) => {
      setActiveChat({
        chatUuid: chatUuidToSet,
        hasAnyMessages: true,
      });
      setActiveAccountUuid(chatAccountUuid ?? undefined);
      setSelectedAccount(undefined);
      setHasAnyMessages(false);
      callbacks?.onChatChange?.(chatUuidToSet);
    },
    [callbacks],
  );

  // Clicking a browser notification opens the chat it belongs to.
  const handleNotificationClick = useCallback(
    (clickedChatUuid: string) => {
      setIsChatVisible(true);
      setIsSettingsOpen(false);
      handleSetActiveChat(clickedChatUuid);
    },
    [handleSetActiveChat, setIsSettingsOpen],
  );

  const notificationController = useChatNotificationController({
    userUuid,
    effectiveAppType,
    serverSettings,
    onPersist: persistSetting,
    isPublicMode,
    isNotificationOwner: enableNotifications && !isReadOnly,
    onNotificationClick: handleNotificationClick,
  });

  const { hasNotifications } = notificationController;
  const refetchChats = notificationController.refetchChats;

  // Hook to mark chats as read when opened
  const markChatAsRead = useMarkChatAsRead();

  const isNewChat = !chatUuid || chatUuid.startsWith('new-chat-');
  const getChatWithMessagesQuery = includeDeletedChat ? GET_DELETED_CHAT_WITH_MESSAGES : GET_CHAT_WITH_MESSAGES;

  const isActiveChat = useCallback(
    (targetChatUuid: string) => normalizeChatUuid(activeChatUuidRef.current) === normalizeChatUuid(targetChatUuid),
    [],
  );
  const beginRegeneration = useCallback((targetChatUuid: string, message: UIMessageWithMetadata) => {
    setPendingRegeneration({ chatUuid: targetChatUuid, message });
  }, []);
  const clearPendingRegeneration = useCallback((targetChatUuid: string) => {
    setPendingRegeneration((current) =>
      current && normalizeChatUuid(current.chatUuid) === normalizeChatUuid(targetChatUuid) ? undefined : current,
    );
  }, []);
  const pendingRegenerationMessage =
    pendingRegeneration && normalizeChatUuid(pendingRegeneration.chatUuid) === normalizeChatUuid(chatUuid)
      ? pendingRegeneration.message
      : undefined;

  // Register the chat this provider has on screen so the notification owner
  // never toasts for a chat the user is already looking at — even when it is
  // shown by a different provider instance (e.g. an embedded panel).
  const viewerKeyRef = useRef<symbol | null>(null);
  viewerKeyRef.current ??= Symbol('nova-provider-view');
  useEffect(() => {
    const viewerKey = viewerKeyRef.current!;
    setActiveChatView(viewerKey, isChatVisible && chatUuid ? normalizeChatUuid(chatUuid) : null);
    return () => clearActiveChatView(viewerKey);
  }, [isChatVisible, chatUuid]);

  // Handle visibility changes
  useEffect(() => {
    onVisibilityChange?.(isChatVisible);
  }, [isChatVisible, onVisibilityChange]);
  const apolloClient = useApolloClient();
  const {
    data,
    networkStatus,
    refetch: refetchChatWithMessages,
  } = useApolloQuery(getChatWithMessagesQuery, {
    variables: { uuid: chatUuid },
    skip: isPublicMode || !chatUuid || isNewChat,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const refetchMessages = useCallback(
    async (targetChatUuid: string) => {
      if (isPublicMode) return;

      // Use an isolated query instead of the active observable query. Its variables
      // can change when the user switches chats while regeneration is still running.
      const result = await apolloClient.query({
        query: getChatWithMessagesQuery,
        variables: { uuid: normalizeChatUuid(targetChatUuid) },
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      });
      // Refresh sidebar metadata in the background; callers only need to wait for messages.
      void refetchChats();
      return result.data?.getChat?.messages.map(backendMessageToSDKMessage);
    },
    [apolloClient, getChatWithMessagesQuery, isPublicMode, refetchChats],
  );
  const rawChatWithMessages = data?.getChat;
  const currentChatWithMessages = getCurrentChatWithMessages({
    chatUuid,
    isNewChat,
    chat: rawChatWithMessages,
  });
  const loading = getIsMessagesLoading({
    chat: rawChatWithMessages,
    currentChat: currentChatWithMessages,
    isNewChat,
    networkStatus,
  });
  // Watch the (polled) chats list as a fallback source for the current chat's
  // active stream. Right after a first message the chat snapshot may not be
  // persisted yet; when polling discovers the server-registered stream, the
  // changed stream id retriggers the resume probe for the open chat.
  const { data: chatsListData } = useApolloQuery(GET_CHATS_BY_FILTERS, {
    variables: { userUuid, appType: effectiveAppType, statusFilter: ACTIVE_CHAT_STATUS },
    skip: isPublicMode || !userUuid,
    fetchPolicy: 'cache-first',
  });
  const currentChatListRow = chatsListData?.chatsList?.find(
    (chat) => normalizeChatUuid(chat.uuid ?? '') === normalizeChatUuid(chatUuid),
  );
  const chatActiveStreamId = currentChatWithMessages?.activeStreamId ?? currentChatListRow?.activeStreamId ?? null;
  const chatHasActiveStream = !!chatActiveStreamId;
  const currentChatAppType = currentChatWithMessages?.appType ?? undefined;
  const persistedChatAccountUuid =
    currentChatWithMessages?.account?.uuid ?? currentChatWithMessages?.accountUuid ?? undefined;
  const effectiveAccountUuid =
    persistedChatAccountUuid ??
    resolveEffectiveAccountUuid({
      activeAccountUuid,
      initialAccountUuid,
      queryAccountUuid,
    });

  // Sync to parent-driven initialChatUuid changes only. Depending on activeChat.chatUuid
  // here would revert user-driven changes (new chat, sidebar selection) back to initialChatUuid.
  const prevInitialChatUuidRef = useRef<string | undefined>(initialChatUuid);
  useEffect(() => {
    if (prevInitialChatUuidRef.current === initialChatUuid) return;
    prevInitialChatUuidRef.current = initialChatUuid;
    if (!initialChatUuid) return;

    setActiveChat({
      chatUuid: initialChatUuid,
      hasAnyMessages: false,
    });
    setActiveAccountUuid(undefined);
    setSelectedAccount(undefined);
    setHasAnyMessages(false);
  }, [initialChatUuid]);

  useEffect(() => {
    if (currentChatWithMessages?.messages) {
      setHasAnyMessages(currentChatWithMessages.messages.length > 0);
    }
  }, [currentChatWithMessages?.messages]);

  // Handle chat overlay becoming visible - mark chat as read if it has unread messages
  const handleChatOverlayVisible = useCallback(() => {
    if (!chatUuid) {
      return;
    }

    const chatUuidWithoutPrefix = normalizeChatUuid(chatUuid);

    if (!isReadOnly) {
      const chats = apolloClient.readQuery({
        query: GET_CHATS_BY_FILTERS,
        variables: { userUuid, appType: effectiveAppType, statusFilter: ACTIVE_CHAT_STATUS },
      });
      const chat = chats?.chatsList?.find(
        (chatItem) => normalizeChatUuid(chatItem.uuid ?? '') === chatUuidWithoutPrefix,
      );
      // If the current chat has unread messages, mark it as read. Keep running
      // workflow streams untouched so reconnect can still attach after navigation.
      // The chat data is already available (optimistically added to sidebar)
      if (shouldMarkChatAsReadOnOpen(chat)) {
        markChatAsRead(chatUuidWithoutPrefix);
      }
    }

    if (!isPublicMode && !isNewChat) {
      void refetchChatWithMessages({ uuid: chatUuidWithoutPrefix });
    }

    // Call the external callback if provided
    onChatOverlayVisible?.(chatUuid);
  }, [
    chatUuid,
    markChatAsRead,
    onChatOverlayVisible,
    apolloClient,
    userUuid,
    effectiveAppType,
    isReadOnly,
    isPublicMode,
    isNewChat,
    refetchChatWithMessages,
  ]);

  const visibleChatActivationKey = getVisibleChatActivationKey({
    chatUuid,
    isChatVisible,
    isNewChat,
  });

  useEffect(() => {
    if (!visibleChatActivationKey) {
      visibleChatActivationKeyRef.current = null;
      return;
    }

    if (visibleChatActivationKeyRef.current === visibleChatActivationKey) {
      return;
    }

    visibleChatActivationKeyRef.current = visibleChatActivationKey;
    handleChatOverlayVisible();
  }, [visibleChatActivationKey, handleChatOverlayVisible]);

  const { data: accountData } = useApolloQuery(GET_ACCOUNT, {
    variables: { uuid: effectiveAccountUuid as string },
    skip: isPublicMode || !effectiveAccountUuid,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const shouldFetchCurrentAccount = isNewChat && !effectiveAccountUuid;
  const { data: currentAccountData } = useApolloQuery(GET_ACCOUNT, {
    variables: { uuid: effectiveAccountUuid as string },
    skip: isPublicMode || !shouldFetchCurrentAccount || !effectiveAccountUuid,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const [setChatAccountMutation] = useMutation(SET_CHAT_ACCOUNT_MUTATION);

  const initialMessages = useMemo(
    () => currentChatWithMessages?.messages.map(backendMessageToSDKMessage) ?? [],
    [currentChatWithMessages?.messages],
  );
  const durableStreamNeedsBaseHydration =
    isDurableNovaChatCompletionsEndpoint(apiEndpoint) &&
    Boolean(chatActiveStreamId) &&
    !isDurableActiveStreamBaseHydrated({
      activeStreamId: chatActiveStreamId,
      chatUuid,
      messages: initialMessages,
    }) &&
    !isDurableActiveStreamAlreadyPersisted({
      activeStreamId: chatActiveStreamId,
      messages: initialMessages,
    });
  const canResumeActiveStream = !durableStreamNeedsBaseHydration;

  useEffect(() => {
    if (!durableStreamNeedsBaseHydration || !chatActiveStreamId || isPublicMode || isNewChat) {
      return undefined;
    }

    const workflowRunId = chatActiveStreamId;
    let cancelled = false;
    const abortController = new AbortController();
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;
    let retryDelayMs = DURABLE_STREAM_BASE_REFETCH_INTERVAL_MS;

    const refetchStreamBase = async () => {
      try {
        // Besides being a headless replay probe, this exact GET lets the
        // backend compare-clear a terminal marker when the workflow failed
        // before it could persist the stamped triggering message.
        await probePersistedDurableActiveStream({
          apiEndpoint,
          chatUuid,
          getToken,
          signal: abortController.signal,
          workflowRunId,
        });
        refetchChats();
      } catch (error) {
        if (abortController.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
          return;
        }
        // The snapshot refetch and next probe handle transient failures.
      }

      try {
        await refetchChatWithMessages({ uuid: normalizeChatUuid(chatUuid) });
      } catch {
        // The next retry handles transient query failures.
      }

      if (!cancelled) {
        retryTimeout = setTimeout(() => void refetchStreamBase(), retryDelayMs);
        retryDelayMs = Math.min(retryDelayMs * 2, MAX_DURABLE_STREAM_BASE_REFETCH_INTERVAL_MS);
      }
    };

    void refetchStreamBase();

    return () => {
      cancelled = true;
      abortController.abort();
      if (retryTimeout !== undefined) {
        clearTimeout(retryTimeout);
      }
    };
  }, [
    apiEndpoint,
    chatActiveStreamId,
    chatUuid,
    durableStreamNeedsBaseHydration,
    getToken,
    isNewChat,
    isPublicMode,
    refetchChats,
    refetchChatWithMessages,
  ]);

  // Determine which account to display (priority: chat data > stored context > current page context)
  const displayAccount = useMemo(() => {
    const account =
      currentChatWithMessages?.account ??
      (effectiveAccountUuid && accountData?.getAccount
        ? { uuid: accountData.getAccount.uuid, name: accountData.getAccount.name }
        : null) ??
      currentAccountData?.getAccount ??
      null;

    return account?.uuid ? { uuid: account.uuid, name: account.name } : undefined;
  }, [currentChatWithMessages?.account, effectiveAccountUuid, accountData?.getAccount, currentAccountData?.getAccount]);

  // Compute placeholder based on account context and skills feature flag
  const isNovaSkillsEnabled = useFeatureFlag('nova-skills', false);
  const placeholder = useMemo(() => {
    const accountNameToUse = displayAccount?.name ?? accountData?.getAccount?.name;
    return getChatPlaceholder({
      accountName: accountNameToUse,
      programType: accountData?.getAccount?.programType,
      skillsEnabled: isNovaSkillsEnabled,
    });
  }, [displayAccount?.name, accountData?.getAccount?.name, accountData?.getAccount?.programType, isNovaSkillsEnabled]);

  // Handle close
  const handleClose = useCallback(() => {
    setIsChatVisible(false);
    onClose?.();
    callbacks?.onFinish?.();
  }, [onClose, callbacks]);

  // Ref to the controller's reconnectIfNeeded — allows handleOpen (defined before the
  // controller) to trigger interactive Outlook re-auth from the user's click gesture.
  const reconnectIfNeededRef = useRef<() => Promise<boolean>>(async () => false);

  // Handle open
  const handleOpen = useCallback(() => {
    setIsChatVisible(true);
    // Trigger Outlook re-auth popup from this click gesture if session expired.
    // The ref is updated after the controller initializes (below).
    reconnectIfNeededRef.current();
  }, []);

  // Handle toggle
  const handleToggle = useCallback(() => {
    if (isChatVisible) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [isChatVisible, handleClose, handleOpen]);

  // Keyboard shortcuts handler
  useEffect(() => {
    if (!isChatVisible) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isSettingsOpen) {
          if (hasActiveSettingsSubView) {
            requestSettingsClose();
          } else {
            setIsSettingsOpen(false);
          }
          return;
        }
        if (isSidebarOpen) {
          return setIsSidebarOpen(false);
        }
        if (isStandaloneMode) {
          return;
        }
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          handleClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    handleClose,
    hasActiveSettingsSubView,
    isFullscreen,
    isStandaloneMode,
    isSidebarOpen,
    isChatVisible,
    isSettingsOpen,
    requestSettingsClose,
    setIsSettingsOpen,
  ]);

  useEffect(() => {
    if (!isNewChat) {
      setSelectedAccount(undefined);
    }
  }, [isNewChat]);

  const startNewChat = useCallback(
    (inputText?: string, inputOptions?: StartNewChatWithInputOptions) => {
      if (isReadOnly) {
        return;
      }

      const accountContext = accountData?.getAccount
        ? {
            uuid: accountData.getAccount.uuid,
            name: accountData.getAccount.name,
          }
        : undefined;

      // Reset account UUID when starting a new chat so it falls back to the page-scoped account context.
      setActiveAccountUuid(undefined);
      setSelectedAccount(undefined);
      setHasAnyMessages(false); // Reset message count for new chat

      // Generate a new chat UUID directly - this ensures a fresh chat every time
      const newChatUuid = `new-chat-${v4()}`;

      setActiveChat({
        chatUuid: newChatUuid,
        accountContext: isStandaloneMode ? undefined : accountContext,
        hasAnyMessages: false,
      });

      const trimmedInputText = inputText?.trim();
      setQueuedNewChatInput(trimmedInputText ? { inputText: trimmedInputText, ...inputOptions } : null);

      callbacks?.onStartNewChat?.(
        accountContext
          ? {
              uuid: accountContext.uuid ?? '',
              name: accountContext.name ?? '',
            }
          : undefined,
      );
    },
    [accountData?.getAccount, isStandaloneMode, callbacks, isReadOnly],
  );

  const handleStartNewChat = useCallback(() => {
    startNewChat();
  }, [startNewChat]);

  const handleStartNewChatWithInput = useCallback(
    (inputText: string, options?: StartNewChatWithInputOptions) => {
      startNewChat(inputText, options);
    },
    [startNewChat],
  );

  const clearQueuedNewChatInput = useCallback(() => {
    setQueuedNewChatInput(null);
  }, []);

  const handleAccountSelect = useCallback(
    async (account: SelectedAccount) => {
      if (isReadOnly) {
        return;
      }

      setSelectedAccount(account);
      const accountUuidToSet = account?.uuid ?? undefined;
      setActiveAccountUuid(accountUuidToSet);

      setActiveChat({
        chatUuid,
        accountContext: account
          ? {
              uuid: account.uuid ?? null,
              name: account.label,
            }
          : { uuid: null },
        hasAnyMessages,
      });

      // If chat has messages, also update the account on the backend
      // This allows setting the account after chat creation, matching the old behavior
      if (account?.uuid && hasAnyMessages) {
        await setChatAccountMutation({
          variables: {
            chatUuid: chatUuid.replace('new-chat-', ''),
            accountUuid: account.uuid,
          },
          optimisticResponse: {
            setChatAccount: {
              uuid: chatUuid,
              account: {
                uuid: account.uuid,
                name: account.label,
              },
            },
          },
        });
      }

      callbacks?.onAccountSelect?.(account);
    },
    [chatUuid, hasAnyMessages, setChatAccountMutation, callbacks, isReadOnly],
  );

  const overlayContextBase = useMemo(
    () => ({
      isSidebarOpen,
      setIsSidebarOpen,
      isFullscreen,
      setIsFullscreen,
      accountUuid: effectiveAccountUuid,
      accountName: accountData?.getAccount?.name,
      accountProgramType: accountData?.getAccount?.programType,
      selectedAccount,
      setAccountUuid: setActiveAccountUuid,
      onSelectAccount: handleAccountSelect,
      canSelectAccount,
      canConnectOutlook,
      isWtwColleague,
      chatUuid,
      isNewChat,
      isReadOnly,
      hasAnyMessages,
      setHasAnyMessages,
      startNewChat: handleStartNewChat,
      startNewChatWithInput: handleStartNewChatWithInput,
      setActiveChat: handleSetActiveChat,
      isStandaloneMode,
      isVisible: isChatVisible,
      onClose: handleClose,
      userUuid,
      userScopes: scopes,
      authProvider,
      apiEndpoint,
      displayAccount: displayAccount ?? undefined,
      placeholder,
      initialMessages,
      isLoadingMessages: loading,
      canResumeActiveStream,
      chatHasActiveStream,
      chatActiveStreamId,
      pendingRegenerationMessage,
      isActiveChat,
      beginRegeneration,
      clearPendingRegeneration,
      refetchMessages,
      chatError,
      setChatError,
      retryLastRequest: retryLastRequest ? handleRetryLastRequest : undefined,
      isRetryingLastRequest,
      setRetryLastRequest,
      hasNotifications,
      additionalContext,
      setAdditionalContext,
      queuedNewChatInput,
      clearQueuedNewChatInput,
      queryAccountUuid,
      pageAccountUuid,
      callbacks,
      notificationController,
      appType,
      effectiveAppType,
      currentChatAppType,
      isPublicMode,
      isSettingsOpen,
      setIsSettingsOpen,
      settingsSection,
      setSettingsSection,
      settingsBackSignal,
      requestSettingsBack,
      requestSettingsClose,
      settingsCloseIntentRef,
      hasActiveSettingsSubView,
      setHasActiveSettingsSubView,
      errorMessage,
    }),
    [
      isSidebarOpen,
      isFullscreen,
      effectiveAccountUuid,
      accountData?.getAccount?.name,
      accountData?.getAccount?.programType,
      selectedAccount,
      canSelectAccount,
      canConnectOutlook,
      isWtwColleague,
      chatUuid,
      isNewChat,
      isReadOnly,
      handleStartNewChat,
      handleStartNewChatWithInput,
      handleAccountSelect,
      handleSetActiveChat,
      hasAnyMessages,
      setHasAnyMessages,
      isStandaloneMode,
      isChatVisible,
      handleClose,
      userUuid,
      scopes,
      authProvider,
      apiEndpoint,
      displayAccount,
      placeholder,
      initialMessages,
      loading,
      canResumeActiveStream,
      chatHasActiveStream,
      chatActiveStreamId,
      pendingRegenerationMessage,
      isActiveChat,
      beginRegeneration,
      clearPendingRegeneration,
      refetchMessages,
      chatError,
      retryLastRequest,
      handleRetryLastRequest,
      isRetryingLastRequest,
      setRetryLastRequest,
      hasNotifications,
      additionalContext,
      queuedNewChatInput,
      clearQueuedNewChatInput,
      queryAccountUuid,
      pageAccountUuid,
      callbacks,
      notificationController,
      appType,
      effectiveAppType,
      currentChatAppType,
      isPublicMode,
      isSettingsOpen,
      setIsSettingsOpen,
      settingsSection,
      settingsBackSignal,
      requestSettingsBack,
      requestSettingsClose,
      hasActiveSettingsSubView,
      errorMessage,
    ],
  );

  // Expose wrapper state for FloatingButton and other external components
  const wrapperState = useMemo<NovaWrapperState>(
    () => ({
      isChatVisible,
      isFloatingButtonVisible,
      hasNotifications,
      handleToggle,
      handleOpen,
      handleClose,
    }),
    [isChatVisible, isFloatingButtonVisible, hasNotifications, handleToggle, handleOpen, handleClose],
  );

  const { outlookConnectEnabled, outlookNeedsReauth, setOutlookConnectEnabled, reconnectIfNeeded } =
    useOutlookConnectController({
      msGraphEnabled,
      delegatedGraphConnectionsEnabled,
      canConnectOutlook,
      isChatVisible,
      notify,
      serverSettings,
      onPersist: persistSetting,
    });
  reconnectIfNeededRef.current = reconnectIfNeeded;

  const overlayContextValue = useMemo(
    () => ({
      ...overlayContextBase,
      outlookConnectEnabled,
      outlookNeedsReauth,
      reconnectOutlook: reconnectIfNeeded,
      setOutlookConnectEnabled,
      serverSettings,
      persistSetting,
      persistSettings,
    }),
    [
      overlayContextBase,
      outlookConnectEnabled,
      outlookNeedsReauth,
      reconnectIfNeeded,
      setOutlookConnectEnabled,
      serverSettings,
      persistSetting,
      persistSettings,
    ],
  );

  return (
    <NovaWrapperContext.Provider value={wrapperState}>
      <ChatOverlayProvider value={overlayContextValue}>{children}</ChatOverlayProvider>
    </NovaWrapperContext.Provider>
  );
}
